import { CHAT_AI_EVENTS, ChatAi } from '@cleartax/wg-library-chat-ai';
import { throttle } from 'lodash';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { WEB_SCHEMA } from '../../../constants/componentStaticData';
import { SecureComponentData } from '../../../constants/partnerPageConst';
import { Meta } from '../../../layout/Meta';
import { PartnerPagesProps } from '../../../pages/clear-frontier/preview/s/[static]';
import { FM_ACTIONS } from '../../../utils/UtilFunctions';
import { handleTrack } from '../../../utils/tracker';
import { If } from '../../Atoms/Conditionals';
import { BlackBanner } from '../../Common/BlackBanner';
import { RECAPTCHA_SITE_KEY } from '../../Common/ContactSalesForm/constants';
import { FaqComponent } from '../../Common/FAQS';
import { SecureComponent } from '../../Common/SecureComponent';
import * as Navigation from '../../Common/navigation';
import { SectionProps } from '../../Common/navigation/types';
import { checkLoginStatus } from '../../Header/utils';
import { ClearInNavbar } from '../../Navbar/ClearNavbar';
import { StartFilingCTA } from '../../Navbar/ClearNavbar/cta';
import { b2cData } from '../../cms/layout/components/header';
import { PartnerPageProvider } from '../context';
import { useForPartnerCall } from '../hooks/partnerPageHooks';
import { useHashScroll } from '../hooks/useScrollHook';
import ChatFilingCard from '../page-sections/chat-filing-card';
import { DirectorMessage } from '../page-sections/director-message-section';
import { EASSection } from '../page-sections/eas-section';
import { EventForm } from '../page-sections/event-registration-form';
import { FileWithCTSection } from '../page-sections/file-with-ct-section';
import { GetReportSection } from '../page-sections/get-report-section';
import { GuidesSection } from '../page-sections/guides-section';
import { HeroSection } from '../page-sections/hero-section';
import { RewardsSection } from '../page-sections/rewards-section';
import { TradingSection } from '../page-sections/trading-section';
import { UseCaseFold } from '../page-sections/use-case-section';
import { IPartnerPlanData } from '../types';
import { getPartnerFaqs } from '../utils';
import { CouponAvail } from '../utils/coupon-avail';
import { useDataForPlans } from '../utils/use-plan-data';
import { extractCoupon, getMetaTitleDescription } from '../utils/utils';
import { CHAT_ENABLED_PAGES } from '../utils/whitelist-pages';

const { publicRuntimeConfig } = getConfig();
const DefaulChattHeaderComp = ({ partnerName = '' }) => {
  return (
    <div
      id="chatheader"
      className="flex font-bold text-base px-6 py-4 items-center border-b border-dotted border-black"
    >
      <div className="flex items-center flex-grow">
        <div className="mr-3">
          <img src="https://assets.clear.in/cleartax/images/1715246701_aichat.svg" />
        </div>
        <div>Help and support</div>
      </div>
      <div>
        <img
          alt="close"
          src="https://assets.clear.in/cleartax/images/1715246787_group1814219280.svg"
          onClick={() => {
            CHAT_AI_EVENTS.updateHandler({
              chatBoxStatus: false,
            });
            handleTrack({
              categoryName: partnerName.toLowerCase(),
              label: 'chat-close',
              obj: null,
            });
          }}
          className="cursor-pointer"
        />
      </div>
    </div>
  );
};
const PartnerPages: React.FC<PartnerPagesProps> = ({ postData, metaData }) => {
  const { seoTitle, seoDesc, seoKeywords } = getMetaTitleDescription(postData);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selfITRPlansData, setSelfITRPlansData] = useState<IPartnerPlanData[]>(
    []
  );
  const [anonId, setAnonId] = useState('');
  const isWhitelisted = CHAT_ENABLED_PAGES.indexOf(postData.slug) >= 0;
  const cardData = useDataForPlans(postData?.self_itr.plans, postData.slug);
  const recaptchaChatRef = useRef<ReCAPTCHA>(null);
  const [couponCode, setCouponCode] = useState<{
    discount: number;
    coupon: string | null;
  }>({
    discount: 0,
    coupon: null,
  });
  const eventRef = useRef(null);
  const fileWithCTRef = useRef<HTMLDivElement>(null);
  const SUBNAV_SCROLL_OFFSET = 95;
  useHashScroll(SUBNAV_SCROLL_OFFSET);
  const couponStyles = `w-full mt-20 z-20 ${
    couponCode.coupon && postData?.self_itr?.active
      ? 'top-[56px] sm:top-[82px]'
      : 'top-0'
  } bg-transparent sticky md:mt-16 sm:mt-16 ${isScrolled ? '' : 'bottom-10'} `;
  useEffect(() => {
    setSelfITRPlansData(cardData);
    setCouponCode(extractCoupon(cardData));
  }, [cardData]);
  const router = useRouter();
  const hideFirstFold = !!router.query?.hideFirstFold;

  const throttledStickySubNav = useMemo(
    () => throttle(() => setIsScrolled(window.scrollY > 250), 200),
    []
  );

  useEffect(() => {
    handleTrack({
      label: window.location.pathname,
      action: FM_ACTIONS.pageview,
      categoryName: postData?.collection,
    });
    if (typeof window === 'undefined') return;
    window.addEventListener('scroll', throttledStickySubNav);
    return () => {
      window.removeEventListener('scroll', throttledStickySubNav);
    };
  }, [throttledStickySubNav]);

  useEffect(() => {
    if (isWhitelisted) {
      checkLoginStatus().then((res) => {
        setAnonId(res?.anon_id);
      });
    }
  }, []);
  const heroSection = (
    <HeroSection
      key={'hero-section'}
      id={'hero-section'}
      name={'Why us?'}
      postData={postData}
      metaData={metaData}
      ref={useRef<HTMLDivElement>(null)}
    />
  );
  const chatFilingSection = (
    <ChatFilingCard
      key={'chat-filing-section'}
      id={'chat-filing-section'}
      name={'Chat Filing'}
      ref={useRef<HTMLDivElement>(null)}
      postData={postData}
      metaData={metaData}
      excludeFromNav={!postData.chatfiling.open_enable}
    />
  );
  const fileWithCTSection = (
    <FileWithCTSection
      key={'file-with-ct-section'}
      id={'file-with-ct'}
      name={'Self Filing Plans'}
      postData={postData}
      metaData={metaData}
      ref={fileWithCTRef}
      excludeFromNav={
        !postData.self_itr.active && !postData.generic_section.active
      }
    />
  );

  const rewardsSection = (
    <RewardsSection
      key={'rewards-section'}
      id={'rewards-section'}
      name={'Rewards & Offers'}
      metaData={metaData}
      ref={useRef<HTMLDivElement>(null)}
      postData={postData}
    />
  );

  const sections2 = [
    <EASSection
      key={'eas-section'}
      id={'eas-section'}
      name={'Expert Assisted Plans'}
      postData={postData}
      metaData={metaData}
      ref={useRef<HTMLDivElement>(null)}
      excludeFromNav={!postData.eas_business.active && !postData.eas_itr.active}
    />,
  ] as unknown as SectionProps[];

  const guidesSection = (
    <GuidesSection
      key={'guides-section'}
      id={'guides-section'}
      name={'Calculators & Resources'}
      ref={useRef<HTMLDivElement>(null)}
      postData={postData}
    />
  );
  const eventRegistrationForm = (
    <EventForm
      key={'event-registration-section'}
      id={'event-registration-section'}
      name={'Webinar'}
      ref={useRef<HTMLDivElement>(null)}
      postData={postData}
    />
  );
  const { onPageClick } = useForPartnerCall({ router });
  const getReCaptchaChatToken = async () => {
    if (!isWhitelisted) return '';

    // Check if ReCAPTCHA component is mounted and ready
    if (!recaptchaChatRef.current) {
      console.warn('[ReCAPTCHA] Component not mounted yet');
      return '';
    }

    try {
      const token = await recaptchaChatRef.current?.executeAsync?.();
      return token ?? '';
    } catch (error) {
      // Handle errors from grecaptcha script (race condition, script not loaded, etc.)
      console.error('[ReCAPTCHA] Execution failed:', error);
      return '';
    }
  };

  return (
    <>
      <div onClick={onPageClick} data-testid="partner-page">
        <Navigation.NavigationContainer
          sections={
            [
              chatFilingSection,
              fileWithCTSection,
              ...sections2,
              rewardsSection,
              guidesSection,
            ] as unknown as SectionProps[]
          }
        >
          <Meta
            title={seoTitle}
            description={seoDesc}
            key="robots"
            keywords={seoKeywords}
            noindex={postData.noindex}
            canonical={`${publicRuntimeConfig.CLEARTAX_DOMAIN}/s/${postData.slug}`}
          />
          <If condition={!hideFirstFold}>
            <div id="subnav-scroll-div" className="h-14"></div>
            <div className="-mt-16">
              <ClearInNavbar
                data={{
                  ...b2cData,
                  rightCTA: (
                    <StartFilingCTA btnText="File Now" href={`/filing/`} />
                  ),
                }}
              />
            </div>
          </If>
          <PartnerPageProvider
            value={{
              selfITRPlansData,
              setSelfITRPlansData,
            }}
          >
            <Navigation.Content>
              <div className="w-full sm:-mt-14">
                <If condition={postData.self_itr.active}>
                  <CouponAvail
                    hideFirstFold={hideFirstFold}
                    setCouponCode={setCouponCode}
                    couponCode={couponCode}
                  />
                </If>
                <If condition={isWhitelisted}>
                  <>
                    <ChatAi getReCaptchaToken={getReCaptchaChatToken}>
                      <ChatAi.ChatSideDrawer
                        positionStyle="top-14 right-0 z-200 border border-dotted border-grey-250 "
                        dimentionStyle="w-[353px] h-[calc(100vh-54px)]"
                      >
                        <>
                          <ReCAPTCHA
                            sitekey={RECAPTCHA_SITE_KEY.chatKey}
                            size="invisible"
                            ref={recaptchaChatRef}
                          />
                          <ChatAi.ChattingCard
                            headerComponent={
                              <DefaulChattHeaderComp
                                partnerName={postData.name}
                              />
                            }
                            containerStyleClass={'bg-white'}
                            contentStyleClass={'h-[calc(100vh_-_178px)]'}
                            chatBoxCardAlwaysOpen
                            contentCustomStyles={{
                              height: 'calc(100vh - 178px)',
                              borderTopWidth: 1,
                            }}
                            checkMessageSide={(messageObj) => {
                              const { sender } = messageObj;
                              return !(sender.displayName === 'ANONYMOUS_USER');
                            }}
                          />
                        </>
                      </ChatAi.ChatSideDrawer>
                    </ChatAi>
                    <div className="flex justify-end fixed bottom-3 sm:bottom-[13%]  right-1 z-[30]">
                      <img
                        src="https://assets.clear.in/cleartax/images/1715337136_group1814218367.svg"
                        alt="Chatbot Logo"
                      />
                      <img
                        src="https://assets.clear.in/cleartax/images/1715754063_frame1814219718.svg"
                        alt="Chatbot Button"
                        onClick={() => {
                          CHAT_AI_EVENTS.updateHandler({
                            aNonId: anonId,
                            chatBoxStatus: true,
                            chatStatus: 'PROGRESS',
                          });
                          handleTrack({
                            categoryName: postData.name.toLowerCase(),
                            label: 'chat-open',
                            obj: this,
                          });
                        }}
                        className="cursor-pointer w-16 h-16"
                      />
                    </div>
                  </>
                </If>
                <If condition={!hideFirstFold}>
                  <div className="wg-container">{heroSection}</div>
                  <div id="subnav" className={couponStyles}>
                    <Navigation.SubNavigation
                      scrollOffset={SUBNAV_SCROLL_OFFSET}
                      isVariant={false}
                      className="px-[0px]"
                      refValue="itr_partner_pages_sec_navbar"
                    />
                  </div>
                </If>
                <DirectorMessage directorMessage={postData.directorMessage} />
                <TradingSection tradingSection={postData.tradingFold} />
                <GetReportSection reportFold={postData.reportFold} />
                <div className="w-full"> {chatFilingSection}</div>
                <div className="w-full bg-grey-190">
                  <div
                    className={`wg-container overflow-x-hidden ${
                      hideFirstFold ? '-mt-14' : ''
                    }`}
                  >
                    {fileWithCTSection}
                  </div>
                </div>
                <If condition={postData.gotYouCoveredFold.active}>
                  <UseCaseFold />
                </If>
                <div className="wg-container overflow-x-hidden">
                  {sections2}
                </div>
                <div className="w-full">{rewardsSection}</div>
                <If condition={postData.blackBanner}>
                  <BlackBanner />
                </If>
                {guidesSection}
                <If condition={!!postData.webinarUrl}>
                  <div ref={eventRef}>{eventRegistrationForm}</div>
                </If>
                <div className="wg-container">
                  <SecureComponent data={SecureComponentData} />
                </div>
                <div className="wg-container">
                  <FaqComponent
                    variant="partner"
                    expanded="firstOnly"
                    FAQS={getPartnerFaqs(postData.faqSection)}
                    faqIconColor={'#314259'}
                  />
                </div>
              </div>
            </Navigation.Content>
          </PartnerPageProvider>
        </Navigation.NavigationContainer>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            WEB_SCHEMA({
              title: seoTitle,
              metaDesc: seoDesc,
              url: `https://cleartax.in/s/${postData.slug}/`,
            })
          ),
        }}
      />
    </>
  );
};

export { PartnerPages };
